"use client";

import { useState } from "react";
import { trackEvent } from "@/lib/analytics-events";

/**
 * The buy path for a physical product (ADR-0092).
 *
 * A VARIANT IS A SELECTOR, NEVER A URL. This is where that clause becomes real:
 * ISH-005's two bundles are radio options on one page, and picking one swaps
 * which checkout link the single CTA points at. Rendering them as separate
 * routes would rebuild the 624-URL city cohort in a commerce costume
 * (ADR-0074/0092).
 *
 * EXACTLY ONE PRIMARY CTA (rule 39). The variant controls are secondary
 * selection, not competing offers — a multi-offer conversion surface converts
 * far worse, so the page must never grow a second primary action.
 *
 * The checkout URLs arrive pre-built from the server, so this island never
 * needs the shop domain and cannot construct a malformed permalink.
 */

export interface BuyVariant {
  readonly id: string;
  readonly sku: string | null;
  readonly title: string;
  readonly price: string;
  readonly availableForSale: boolean;
  /** Null when the shop domain is unset or the id is not a variant GID. */
  readonly checkoutUrl: string | null;
}

export default function ProductBuyPanel({
  variants,
  productTitle,
}: {
  variants: readonly BuyVariant[];
  productTitle: string;
}) {
  const firstBuyable = variants.find((v) => v.availableForSale && v.checkoutUrl);
  const [selectedId, setSelectedId] = useState(firstBuyable?.id ?? variants[0]?.id);
  const selected = variants.find((v) => v.id === selectedId) ?? variants[0];

  if (!selected) return null;

  const canBuy = selected.availableForSale && Boolean(selected.checkoutUrl);

  return (
    <div>
      {variants.length > 1 ? (
        <fieldset className="mb-4 border-0 p-0">
          <legend className="mb-2 text-sm font-medium">Choose your bundle</legend>
          {variants.map((variant) => (
            <label key={variant.id} className="mb-2 flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="variant"
                value={variant.id}
                checked={variant.id === selected.id}
                onChange={() => setSelectedId(variant.id)}
                disabled={!variant.availableForSale}
              />
              <span>
                {variant.title} — {variant.price}
                {variant.availableForSale ? "" : " (out of stock)"}
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}

      {canBuy ? (
        <a
          href={selected.checkoutUrl as string}
          data-testid="product-buy-cta"
          onClick={() =>
            trackEvent("commerce_checkout_started", {
              offer_type: "product",
              commerce_system: "shopify",
              checkout_channel: "shopify_product",
              product_sku: selected.sku,
              product_name: productTitle,
              variant_name: selected.title,
              placement: "product_detail_primary",
              source_path: window.location.pathname,
              is_primary_cta: true,
            })
          }
          className="inline-flex min-h-11 items-center justify-center rounded-md bg-surface-brand-strong px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ish-accent focus-visible:ring-offset-2"
        >
          Buy {productTitle} — {selected.price}
        </a>
      ) : (
        <p data-testid="product-buy-unavailable">This option is out of stock right now.</p>
      )}
    </div>
  );
}
